<template>
  <div class="setup-page">
    <v-card
      width="460"
      class="setup-card">
      <v-card-title class="setup-title">Set up your TastingIQ account</v-card-title>

      <v-card-text>
        <div
          v-if="loading"
          class="status-row"
          role="status"
          aria-live="polite">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mr-2"
          />
          Checking your invitation...
        </div>

        <div
          v-if="error"
          class="error-message"
          role="alert">
          {{ error }}
        </div>

        <v-form
          v-if="invitationLoaded"
          ref="setupForm"
          v-model="formValid"
          class="setup-form"
          @submit.prevent="acceptInvitation">
          <p>
            Create a password to request {{ programName }} tasting events for
            {{ clientName }}.
          </p>

          <v-text-field
            v-model="username"
            label="Your assigned username"
            autocomplete="username"
            readonly
            required
          />
          <p class="field-help">
            Save this username. You will use it whenever you sign in to TastingIQ.
          </p>

          <v-text-field
            ref="passwordField"
            v-model="password"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :rules="passwordRules"
            :type="showPassword ? 'text' : 'password'"
            class="setup-field"
            autocomplete="new-password"
            label="Create password"
            maxlength="255"
            required
            @click:append="showPassword = !showPassword"
          />
          <v-text-field
            v-model="confirmation"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :rules="confirmationRules"
            :type="showPassword ? 'text' : 'password'"
            class="setup-field"
            autocomplete="new-password"
            label="Confirm password"
            maxlength="255"
            required
            @click:append="showPassword = !showPassword"
          />
          <p class="field-help">Use 12 to 255 characters. Both password fields must match.</p>

          <v-btn
            :disabled="!formValid"
            :loading="submitting"
            type="submit"
            color="primary"
            block>
            Set Password and Continue
          </v-btn>
        </v-form>

        <v-btn
          v-if="showLoginLink"
          :to="loginDestination"
          color="primary"
          class="mt-3"
          @click="rememberUsernameForLogin">
          Sign In to TastingIQ
        </v-btn>
        <p
          v-if="showLoginLink"
          class="field-help login-username">
          Username: <strong>{{ username }}</strong>
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data () {
    return {
      confirmation: '',
      clientName: '',
      error: null,
      formValid: false,
      invitationLoaded: false,
      invitationToken: '',
      loading: true,
      password: '',
      programName: 'Event Request',
      showLoginLink: false,
      showPassword: false,
      submitting: false,
      username: ''
    }
  },

  computed: {
    confirmationRules () {
      return [
        value => !!value || 'Confirm your password',
        value => value === this.password || 'Passwords do not match'
      ]
    },

    loginDestination () {
      return {
        path: '/login',
        query: {
          return: '/event-request'
        }
      }
    },

    passwordRules () {
      return [
        value => !!value || 'Password is required',
        value => (value && value.length >= 12) || 'Password must be at least 12 characters',
        value => (value && value.length <= 255) || 'Password must be no more than 255 characters'
      ]
    }
  },

  created () {
    this.loadInvitation()
  },

  methods: {
    rememberUsernameForLogin () {
      if (this.username) {
        window.sessionStorage.setItem('tastingiq_login_username', this.username)
      }
    },

    responseMessage (error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : ((error && error.message) || fallback)
    },

    async loadInvitation () {
      const storageKey = 'tastingiq_one_time_invitation'
      const earlyToken = window.__TASTINGIQ_ONE_TIME_TOKEN__
      const fragment = new URLSearchParams(window.location.hash.slice(1))
      this.invitationToken = earlyToken && earlyToken.purpose === 'invitation'
        ? earlyToken.token
        : (fragment.get('token') || window.sessionStorage.getItem(storageKey) || '')
      if (this.invitationToken) window.sessionStorage.setItem(storageKey, this.invitationToken)
      delete window.__TASTINGIQ_ONE_TIME_TOKEN__
      window.history.replaceState(null, '', window.location.pathname + window.location.search)

      if (!this.invitationToken) {
        this.loading = false
        this.error = 'Reopen the original invitation link. If it has expired, ask HSP to create a new invitation link.'
        return
      }

      try {
        await this.$store.dispatch('get_config')
        const response = await axios.post(
          this.$store.state.apiRoot + 'auth/invitations/inspect',
          { token: this.invitationToken },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const result = response.data || {}
        if (result.status !== 'success' || !result.invitation) {
          throw new Error(result.message || 'This invitation is invalid or has expired.')
        }

        this.username = result.invitation.username
        this.clientName = result.invitation.client_name
        this.programName = result.invitation.event_request_program_name || 'Event Request'
        this.invitationLoaded = true
        await this.$nextTick()
        if (this.$refs.passwordField) this.$refs.passwordField.focus()
      } catch (error) {
        if (error && error.response && error.response.status === 400) {
          window.sessionStorage.removeItem(storageKey)
        }
        this.error = this.responseMessage(
          error,
          'This invitation is invalid or has expired. Ask HSP to create a new invitation link.'
        )
      } finally {
        this.loading = false
      }
    },

    async acceptInvitation () {
      if (!this.$refs.setupForm || !this.$refs.setupForm.validate()) return

      this.error = null
      this.submitting = true
      try {
        const response = await axios.post(
          this.$store.state.apiRoot + 'auth/invitations/accept',
          {
            token: this.invitationToken,
            password: this.password
          },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const result = response.data || {}
        if (result.status !== 'success' || !result.login_handoff_token) {
          throw new Error(result.message || 'The invitation could not be accepted.')
        }

        window.sessionStorage.removeItem('tastingiq_one_time_invitation')
        window.sessionStorage.setItem('tastingiq_login_username', result.username || this.username)
        const destination = new URL(window.location.origin + '/complete-login')
        destination.hash = 'token=' + encodeURIComponent(result.login_handoff_token)
        window.location.replace(destination.toString())
      } catch (error) {
        const status = error && error.response ? error.response.status : 0
        const retryable = status === 0 || status >= 500
        this.invitationLoaded = retryable
        this.showLoginLink = !retryable && !!this.username
        this.error = this.responseMessage(
          error,
          'Account setup could not be completed. If your password was saved, sign in with the username shown above; otherwise ask HSP for a new invitation link.'
        )
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.setup-page {
  align-items: flex-start;
  background: #f4f3fb;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: 8vh 16px 32px;
}

.setup-card {
  padding: 16px;
}

.setup-title {
  color: #19135c;
  font-size: 24px;
}

.status-row {
  align-items: center;
  display: flex;
}

.error-message {
  background: #ffebee;
  border-radius: 4px;
  color: #b71c1c;
  margin-bottom: 16px;
  padding: 10px 12px;
}

.field-help {
  color: #555;
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 18px;
}

.setup-field {
  margin-bottom: 6px;
}

.setup-form >>> .v-text-field__details {
  min-height: 18px;
  overflow: visible;
}

.setup-form >>> .v-messages__message {
  line-height: 1.4;
}

.login-username {
  margin-top: 12px;
}
</style>
